import { io, Socket } from 'socket.io-client';

export interface ChatMessage { id: string; senderType: 'LEAD' | 'AI' | 'WORKER' | 'SYSTEM'; content: string; createdAt: string }

interface Handlers {
  onMessage: (message: ChatMessage) => void;
  onTyping: () => void;
  onError: (kind: 'offline' | 'send') => void;
  /** Called on every (re)connection, so missed messages can be fetched again. */
  onConnect: () => void;
  onClosed: () => void;
}

export class ChatSocketClient {
  private socket: Socket | null = null;
  private chatId = '';
  private offline = false;
  private joined = false;
  private outbox: { content: string; meta?: Record<string, string> }[] = [];

  constructor(private tenantId: string, private apiUrl: string, private handlers: Handlers) {}

  connect(chatId: string, leadId: string) {
    this.chatId = chatId;
    this.socket = io(this.apiUrl);
    this.socket.on('connect', () => {
      this.offline = false;
      this.socket?.emit('join_chat', { tenantId: this.tenantId, leadId, chatId });
      this.joined = true;
      // Messages typed before the connection was ready go out after joining the chat room.
      this.outbox.splice(0).forEach(item => this.emitMessage(item.content, item.meta));
      this.handlers.onConnect();
    });
    this.socket.on('disconnect', () => { this.joined = false; });
    this.socket.on('new_message', (message: ChatMessage) => this.handlers.onMessage(message));
    this.socket.on('typing', (data: { senderType?: string }) => { if (data?.senderType === 'AI') this.handlers.onTyping(); });
    this.socket.on('chat_closed', () => this.handlers.onClosed());
    this.socket.on('connect_error', () => {
      if (!this.offline) this.handlers.onError('offline');
      this.offline = true;
    });
    this.socket.on('error', () => this.handlers.onError('send'));
  }

  get connected() { return Boolean(this.socket?.connected); }

  /** `meta` carries a structured choice (tapped slot or service) next to the readable text. */
  sendMessage(content: string, meta?: Record<string, string>) {
    if (this.joined) this.emitMessage(content, meta);
    // A retry while offline must not queue the same message twice.
    else if (!this.outbox.some(item => item.content === content)) this.outbox.push({ content, meta });
  }

  private emitMessage(content: string, meta?: Record<string, string>) {
    this.socket?.emit('send_message', { chatId: this.chatId, tenantId: this.tenantId, senderType: 'LEAD', content, meta });
  }

  disconnect() {
    this.socket?.disconnect();
    this.socket = null;
  }
}
